import equal from "fast-deep-equal/es6";
import { computed } from "mobx";

import { DeviceDetailsClient } from "@formant/core/client/DeviceDetailsClient";
import { defined } from "@formant/core/common/defined";
import { matchesTags } from "@formant/core/common/matchesTags";
import { IDeviceDetails } from "@formant/core/model/IDeviceDetails";
import { IDeviceQuery } from "@formant/core/model/IDeviceQuery";
import { ITags } from "@formant/core/model/ITags";
import { Uuid } from "@formant/core/model/Uuid";

import { IAppStore } from "./IAppStore";
import { IAuthStore } from "./IAuthStore";
import { IDeviceDetailsStore } from "./IDeviceDetailsStore";
import { IOnlineDevicesStore } from "./IOnlineDevicesStore";
import { StoreCache } from "./StoreCache";

export class DeviceDetailsStore implements IDeviceDetailsStore {
    private listCache = new StoreCache<
        IDeviceQuery | undefined,
        IDeviceDetails[]
    >({
        isEqual: equal,
    });

    constructor(
        private deviceDetailsClient: DeviceDetailsClient,
        private authStore: IAuthStore,
        private appStore: IAppStore,
        private onlineDevicesStore: IOnlineDevicesStore,
    ) {}

    @computed
    public get byId() {
        const all = this.list();
        if (!all) {
            return undefined;
        }
        return all.reduce<{ [id: string]: IDeviceDetails }>((acc, device) => {
            acc[device.id] = device;
            return acc;
        }, {});
    }

    list(query?: IDeviceQuery): IDeviceDetails[] | undefined {
        return this.listCache.get(query, () => this.fetchList(query));
    }

    get(id: Uuid): IDeviceDetails | false | undefined {
        const byId = this.byId;
        if (!byId) {
            return undefined;
        }
        return byId[id] || false;
    }

    refreshList(query?: IDeviceQuery) {
        return this.listCache.refresh(query, () => this.fetchList(query), {
            lazy: true,
        });
    }

    // lists matching devices given the tags of a view
    listMatching(
        viewTags: ITags,
        deviceQuery: IDeviceQuery = { enabled: true, type: "default" },
    ): IDeviceDetails[] | undefined {
        const all = this.list(deviceQuery);
        if (!all) {
            return undefined;
        }
        // return all devices that have at least one tag in common with a view
        // or have no tags at all.
        // If the view has no tags it will show all devices.
        return all.filter(_ =>
            matchesTags(_.tags, viewTags, {
                logic: "or",
                ignoreReservedTags: true,
            }),
        );
    }

    recent(): IDeviceDetails[] | undefined {
        const enabled = this.list({
            enabled: true,
            type: "default",
            fullyConfigured: false,
        });
        const online = this.onlineDevicesStore.online();
        return this.handleRecentResponse(enabled, online);
    }

    async recentAsync(): Promise<IDeviceDetails[]> {
        const [enabled, online, byId] = await Promise.all([
            this.fetchList({
                enabled: true,
                type: "default",
                fullyConfigured: false,
            }),
            this.onlineDevicesStore.fetch(),
            this.byIdAsync(),
        ]);

        return this.handleRecentResponse(enabled, online, byId, 3000) || [];
    }

    async listAsync(query?: IDeviceQuery): Promise<IDeviceDetails[]> {
        return this.listCache.getAsync(query, () => this.fetchList(query));
    }

    clear() {
        this.listCache.clear();
        this.list();
    }

    async fetchList(query?: IDeviceQuery): Promise<IDeviceDetails[]> {
        return await this.deviceDetailsClient.list(
            await this.authStore.token(),
            query,
        );
    }

    private handleRecentResponse(
        enabled: IDeviceDetails[] | undefined,
        online: Set<Uuid> | undefined,
        byId = this.byId,
        max = 30,
    ) {
        if (
            byId === undefined ||
            enabled === undefined ||
            online === undefined
        ) {
            return undefined;
        }

        const enabledIds = new Set(
            enabled.map(_ => defined(_.id)).filter(_ => !!byId[_]),
        );
        const favoriteIds = new Set(
            this.appStore.favorites.filter(_ => enabledIds.has(_)),
        );
        const onlineIds = new Set(
            [...online].filter(_ => enabledIds.has(_) && !favoriteIds.has(_)),
        );
        const recentIds = new Set(
            this.appStore.recentDeviceIds.filter(
                _ =>
                    enabledIds.has(_) &&
                    !favoriteIds.has(_) &&
                    !onlineIds.has(_),
            ),
        );
        const otherIds = new Set(
            [...enabledIds].filter(
                _ =>
                    !favoriteIds.has(_) &&
                    !onlineIds.has(_) &&
                    !recentIds.has(_),
            ),
        );

        const group = (ids: Set<Uuid>) =>
            [...ids]
                .map(_ => defined(byId[_]))
                .sort((a, b) => a.name.localeCompare(b.name));

        return [
            ...group(favoriteIds),
            ...group(onlineIds),
            ...group(
                new Set(
                    [...recentIds, ...otherIds].slice(
                        0,
                        max - favoriteIds.size - onlineIds.size,
                    ),
                ),
            ),
        ].slice(0, max);
    }

    private async byIdAsync() {
        const all = await this.fetchList();
        if (!all) {
            return undefined;
        }
        return all.reduce<{ [id: string]: IDeviceDetails }>((acc, device) => {
            acc[device.id] = device;
            return acc;
        }, {});
    }
}
